"use client";

// ─── Modal zakupu kursu jako prezent ─────────────────────────────
// Obsługuje dwa sposoby przekazania:
//   • "direct" — przypisanie bezpośrednio do istniejącego konta
//     (weryfikacja adresu e-mail odbiorcy przed płatnością),
//   • "code"   — wygenerowanie jednorazowego kodu prezentowego.
// Po opłaceniu kupujący nie otrzymuje dostępu; kurs trafia do odbiorcy
// lub generowany jest kod. Pełna walidacja, obsługa błędów, a11y.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Gift,
  X,
  Mail,
  MessageSquare,
  Ticket,
  User,
  Loader2,
  Check,
  Copy,
  ArrowRight,
  AlertCircle,
  ShieldCheck,
  Sparkles,
  ExternalLink,
  CreditCard,
} from "lucide-react";
import Link from "next/link";
import { useToast } from "@/lib/toast";

type DeliveryMethod = "direct" | "code";
type Phase = "form" | "processing" | "success";

interface GiftCourse {
  id: string;
  title: string;
  slug: string;
  price: number;
  originalPrice?: number;
  thumbnail?: string;
  cardColor?: string;
}

interface GiftSuccess {
  deliveryMethod: DeliveryMethod;
  code: string | null;
  recipientEmail: string;
  courseTitle: string;
}

interface CheckoutGateway {
  provider: string;
  label: string;
  mode: string;
  methods: Record<string, boolean>;
  isSimulation: boolean;
}

// Metody płatności z logotypami (jak w koszyku). BLIK — czarne tło, Przelewy24 — przezroczyste.
const METHOD_META: Record<string, { label: string; logo: string; logoAlt: string; logoClass: string }> = {
  blik: {
    label: "BLIK",
    logo: "https://upload.wikimedia.org/wikipedia/commons/7/7c/Blik_logo.jpg",
    logoAlt: "BLIK",
    logoClass: "h-6 rounded-md",
  },
  transfer: {
    label: "Szybki przelew",
    logo: "https://upload.wikimedia.org/wikipedia/commons/e/e3/Przelewy24_logo.png",
    logoAlt: "Przelewy24",
    logoClass: "h-4",
  },
};

export default function GiftPurchaseModal({
  open,
  onClose,
  course,
}: {
  open: boolean;
  onClose: () => void;
  course: GiftCourse;
}) {
  const { toast } = useToast();
  const [method, setMethod] = useState<DeliveryMethod>("direct");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState<Phase>("form");
  const [error, setError] = useState("");
  const [verifiedName, setVerifiedName] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState<GiftSuccess | null>(null);
  const [copied, setCopied] = useState(false);
  const [gateway, setGateway] = useState<CheckoutGateway | null>(null);
  const [payMethod, setPayMethod] = useState<string>("");
  const [processingMessage, setProcessingMessage] = useState("Realizujemy Twój prezent…");
  // Zgody kupującego: regulamin/polityka + (jeśli kurs wymaga) zrzeczenie prawa do odstąpienia.
  const [requireWaiver, setRequireWaiver] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [withdrawalWaiver, setWithdrawalWaiver] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const accent = course.cardColor || "#7c3aed";
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Reset przy zamknięciu/otwarciu.
  useEffect(() => {
    if (open) {
      setMethod("direct");
      setEmail("");
      setMessage("");
      setPhase("form");
      setError("");
      setVerifiedName(null);
      setSuccess(null);
      setCopied(false);
      setAcceptedTerms(false);
      setWithdrawalWaiver(false);
      setProcessingMessage("Realizujemy Twój prezent…");
    }
  }, [open]);

  // Czy kurs wymaga zgody na odstąpienie od prawa zwrotu (treści cyfrowe).
  useEffect(() => {
    if (!open || !course.id) return;
    let cancelled = false;
    fetch(`/api/checkout/requirements?ids=${encodeURIComponent(course.id)}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const found = Array.isArray(d.courses)
          ? d.courses.find((c: { id: string }) => c.id === course.id)
          : null;
        setRequireWaiver(!!found?.requireWithdrawalWaiver);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, course.id]);

  // Aktywna bramka płatności + dostępne metody (jak w koszyku).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetch("/api/checkout/gateway")
      .then((r) => r.json())
      .then((d) => {
        if (cancelled || !d || d.error) return;
        setGateway(d as CheckoutGateway);
        const active = Object.keys(d.methods || {}).filter((k) => d.methods[k] && METHOD_META[k]);
        const preferred = ["blik", "transfer"].find((m) => active.includes(m)) || active[0] || "";
        setPayMethod(preferred);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Blokada przewijania tła + Escape.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && phase !== "processing") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, phase, onClose]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  // Metody płatności aktywnej bramki (z metadanymi UI).
  const availableMethods = gateway
    ? Object.keys(gateway.methods).filter((k) => gateway.methods[k] && METHOD_META[k])
    : [];

  const consentOk = acceptedTerms && (!requireWaiver || withdrawalWaiver);

  async function verifyRecipient() {
    setError("");
    if (!emailValid) {
      setError("Podaj poprawny adres e-mail");
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch("/api/gifts/recipient", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setVerifiedName(null);
        setError(data.error || "Nie znaleziono użytkownika o podanym adresie e-mail");
        return;
      }
      setVerifiedName(data.recipient?.name || email.trim());
    } catch {
      setError("Wystąpił błąd weryfikacji. Spróbuj ponownie.");
    } finally {
      setVerifying(false);
    }
  }

  async function handlePay() {
    setError("");
    if (method === "direct" && !verifiedName) {
      await verifyRecipient();
      return;
    }
    if (!consentOk) {
      setError("Zaznacz wymagane zgody, aby kontynuować.");
      return;
    }
    setProcessingMessage("Realizujemy Twój prezent…");
    setPhase("processing");
    try {
      const res = await fetch("/api/gifts/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          courseId: course.id,
          deliveryMethod: method,
          recipientEmail: method === "direct" ? email.trim() : undefined,
          message: message.trim() || undefined,
          method: payMethod || undefined,
          acceptedTerms,
          withdrawalWaiver: requireWaiver ? withdrawalWaiver : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.failureReason || "Nie udało się zrealizować zakupu");
      }

      // Realna bramka płatnicza (np. Przelewy24) — przekierowanie do operatora.
      // Prezent zostanie zrealizowany po potwierdzeniu płatności (webhook).
      if (data.redirectUrl) {
        setProcessingMessage(`Przekierowywanie do ${gateway?.label ?? "operatora płatności"}…`);
        await new Promise((r) => setTimeout(r, 800));
        window.location.href = data.redirectUrl;
        return;
      }

      // Tryb testowy (bez przekierowania) — prezent zrealizowany od razu.
      const result: GiftSuccess = {
        deliveryMethod: method,
        code: data.gift?.code ?? null,
        recipientEmail: email.trim(),
        courseTitle: course.title,
      };
      setSuccess(result);
      setPhase("success");

      toast({
        variant: "gift",
        title: course.title,
        description:
          method === "code"
            ? "Kod prezentowy został wygenerowany"
            : `Prezent dla ${verifiedName || email.trim()} został dostarczony`,
        action: { label: "Moje prezenty", href: "/platforma/panel/prezenty" },
        duration: 8000,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Wystąpił błąd");
      setPhase("form");
    }
  }

  function copyCode() {
    if (!success?.code) return;
    navigator.clipboard?.writeText(success.code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {}
    );
  }

  // UWAGA: portal MUSI opakowywac AnimatePresence, a nie odwrotnie.
  // AnimatePresence filtruje swoje dzieci przez React.isValidElement(), a wynik
  // createPortal() ma $$typeof === "react.portal" (nie "react.element"), wiec
  // bylby odrzucony -> modal NIGDY by sie nie pokazal (na zadnej stronie).
  // Dlatego: createPortal(<AnimatePresence>...</AnimatePresence>, document.body).
  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          key="gift-modal"
          className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => phase !== "processing" && onClose()}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
          />

          {/* Dialog */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={`Kup kurs ${course.title} jako prezent`}
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 280, damping: 26 }}
            className="relative z-10 flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl dark:bg-slate-900 sm:rounded-3xl"
          >
            {/* Header */}
            <div
              className="relative shrink-0 px-6 py-5 text-white"
              style={{ background: `linear-gradient(135deg, ${accent} 0%, #6d28d9 100%)` }}
            >
              <button
                type="button"
                onClick={() => phase !== "processing" && onClose()}
                aria-label="Zamknij"
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
              >
                <X size={16} />
              </button>
              <div className="flex items-center gap-2">
                <Gift size={20} />
                <h2 className="text-lg font-bold">Kup jako prezent</h2>
              </div>
              <p className="mt-1 text-sm text-white/80 line-clamp-1">{course.title}</p>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <AnimatePresence mode="wait">
                {phase === "success" && success ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-2 text-center"
                  >
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30">
                      <Check size={32} className="text-white" />
                    </div>
                    <h3 className="text-xl font-extrabold text-slate-800 dark:text-white">
                      {success.deliveryMethod === "code"
                        ? "Kod prezentowy gotowy!"
                        : "Prezent dostarczony!"}
                    </h3>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">
                      {success.deliveryMethod === "code"
                        ? "Przekaż poniższy kod osobie obdarowanej. Wysłaliśmy go również na Twój adres e-mail."
                        : `Kurs został przypisany do konta odbiorcy (${success.recipientEmail}). Powiadomienie e-mail zostało wysłane.`}
                    </p>

                    {success.deliveryMethod === "code" && success.code && (
                      <div className="mx-auto mt-5 max-w-xs">
                        <div className="rounded-2xl border border-dashed border-violet-300 bg-violet-50 p-4 dark:border-violet-500/40 dark:bg-violet-500/10">
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-violet-500">
                            Kod prezentowy
                          </p>
                          <p className="font-mono text-2xl font-extrabold tracking-[0.18em] text-slate-800 dark:text-white">
                            {success.code}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={copyCode}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                        >
                          {copied ? <Check size={14} /> : <Copy size={14} />}
                          {copied ? "Skopiowano" : "Kopiuj kod"}
                        </button>
                      </div>
                    )}

                    <div className="mt-6 flex flex-col gap-2">
                      <Link
                        href="/platforma/panel/prezenty"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-white transition-colors hover:bg-primary/90"
                      >
                        <Sparkles size={16} /> Przejdź do moich prezentów
                      </Link>
                      <button
                        type="button"
                        onClick={onClose}
                        className="w-full rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                      >
                        Zamknij
                      </button>
                    </div>
                  </motion.div>
                ) : phase === "processing" ? (
                  <motion.div
                    key="processing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center py-12 text-center"
                  >
                    <Loader2 size={36} className="animate-spin text-primary" />
                    <p className="mt-4 text-sm font-medium text-slate-600 dark:text-slate-300">
                      {processingMessage}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-5"
                  >
                    {/* Wybór sposobu przekazania */}
                    <div>
                      <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Sposób przekazania
                      </p>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <MethodOption
                          active={method === "direct"}
                          onClick={() => {
                            setMethod("direct");
                            setError("");
                          }}
                          icon={<User size={18} />}
                          title="Bezpośrednio"
                          desc="Przypisz kurs do konta odbiorcy"
                          accent={accent}
                        />
                        <MethodOption
                          active={method === "code"}
                          onClick={() => {
                            setMethod("code");
                            setError("");
                          }}
                          icon={<Ticket size={18} />}
                          title="Kod prezentowy"
                          desc="Wygeneruj kod do przekazania"
                          accent={accent}
                        />
                      </div>
                    </div>

                    {/* E-mail odbiorcy (tylko direct) */}
                    {method === "direct" && (
                      <div>
                        <label
                          htmlFor="gift-email"
                          className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-200"
                        >
                          Adres e-mail osoby obdarowanej
                        </label>
                        <div className="relative">
                          <Mail
                            size={16}
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            id="gift-email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setVerifiedName(null);
                              setError("");
                            }}
                            placeholder="np. anna@example.com"
                            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-800 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                          />
                        </div>
                        {verifiedName && (
                          <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                            <ShieldCheck size={14} /> Znaleziono użytkownika: {verifiedName}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Wiadomość */}
                    <div>
                      <label
                        htmlFor="gift-message"
                        className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"
                      >
                        <MessageSquare size={14} /> Wiadomość (opcjonalnie)
                      </label>
                      <textarea
                        id="gift-message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={3}
                        maxLength={1000}
                        placeholder="Napisz kilka słów do osoby obdarowanej..."
                        className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                      />
                    </div>

                    {/* Metoda płatności — realna bramka (jak w koszyku) lub symulacja testowa */}
                    <div>
                      <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Metoda płatności
                      </p>
                      {gateway && !gateway.isSimulation ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
                          {availableMethods.length > 0 && (
                            <div
                              className={`grid gap-2 ${
                                availableMethods.length >= 2 ? "grid-cols-2" : "grid-cols-1"
                              }`}
                            >
                              {availableMethods.map((m) => {
                                const meta = METHOD_META[m];
                                const active = payMethod === m;
                                return (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => setPayMethod(m)}
                                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-medium transition ${
                                      active
                                        ? "bg-white dark:bg-slate-900"
                                        : "border-slate-200 text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:text-slate-300"
                                    }`}
                                    style={active ? { borderColor: accent, color: accent } : undefined}
                                  >
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                      src={meta.logo}
                                      alt={meta.logoAlt}
                                      loading="lazy"
                                      className={`${meta.logoClass} w-auto shrink-0 object-contain`}
                                    />
                                    <span>{meta.label}</span>
                                    {active && (
                                      <Check size={15} className="ml-auto" style={{ color: accent }} />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {/* Wybór metody bezpośrednio w bramce operatora */}
                          <button
                            type="button"
                            onClick={() => setPayMethod("")}
                            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-300"
                            style={payMethod === "" ? { color: accent } : undefined}
                          >
                            <ExternalLink size={12} />
                            Wybiorę metodę w {gateway.label}
                          </button>
                          <p className="mt-2 text-[10px] text-slate-400">
                            Obsługiwane przez {gateway.label}. Płatność dokończysz w bezpiecznej bramce
                            operatora.
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <CreditCard size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                              Symulacja płatności
                            </p>
                            <p className="text-[10px] text-slate-400">Środowisko testowe</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Zgody wymagane do zakupu */}
                    <div className="rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-700 dark:bg-slate-800/60">
                      <p className="mb-2.5 flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <ShieldCheck size={14} className="text-primary" /> Zgody wymagane do zakupu
                      </p>
                      <div className="space-y-2.5">
                        <label className="flex cursor-pointer items-start gap-2.5">
                          <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-violet-600"
                          />
                          <span className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                            Akceptuję{" "}
                            <a href="/dokumenty/regulamin" target="_blank" rel="noreferrer" className="font-semibold text-violet-600 underline">
                              regulamin
                            </a>{" "}
                            oraz{" "}
                            <a href="/dokumenty/polityka-prywatnosci" target="_blank" rel="noreferrer" className="font-semibold text-violet-600 underline">
                              politykę prywatności
                            </a>
                            .
                          </span>
                        </label>
                        {requireWaiver && (
                          <label className="flex cursor-pointer items-start gap-2.5">
                            <input
                              type="checkbox"
                              checked={withdrawalWaiver}
                              onChange={(e) => setWithdrawalWaiver(e.target.checked)}
                              className="mt-0.5 h-4 w-4 shrink-0 accent-violet-600"
                            />
                            <span className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                              Żądam udostępnienia kursu „<strong>{course.title}</strong>” obdarowanej osobie
                              natychmiast po zakupie i przyjmuję do wiadomości, że w związku z tym{" "}
                              <strong>tracę prawo do odstąpienia od umowy</strong> (treści cyfrowe).
                            </span>
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Błąd */}
                    {error && (
                      <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer (akcja) */}
            {phase === "form" && (
              <div className="shrink-0 border-t border-slate-100 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-900/60">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm text-slate-500 dark:text-slate-400">Do zapłaty</span>
                  <span className="text-xl font-extrabold text-slate-800 dark:text-white">
                    {course.price > 0 ? `${course.price.toFixed(0)} zł` : "Za darmo"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handlePay}
                  disabled={
                    verifying ||
                    (method === "direct" && !emailValid) ||
                    ((method === "code" || !!verifiedName) && !consentOk)
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ backgroundColor: accent }}
                >
                  {verifying ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Weryfikacja odbiorcy...
                    </>
                  ) : method === "direct" && !verifiedName ? (
                    <>
                      Sprawdź odbiorcę i przejdź dalej <ArrowRight size={18} />
                    </>
                  ) : (
                    <>
                      <Gift size={18} /> Zapłać i podaruj
                    </>
                  )}
                </button>
                <p className="mt-2 text-center text-xs text-slate-400">
                  🔒{" "}
                  {gateway && !gateway.isSimulation
                    ? `Płatność w bezpiecznej bramce ${gateway.label}`
                    : "Bezpieczna płatność"}{" "}
                  · kupujący nie otrzymuje kopii kursu
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function MethodOption({
  active,
  onClick,
  icon,
  title,
  desc,
  accent,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  desc: string;
  accent: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex items-start gap-2.5 rounded-xl border-2 p-3 text-left transition-all ${
        active
          ? "bg-slate-50 dark:bg-slate-800"
          : "border-slate-200 hover:border-slate-300 dark:border-slate-700"
      }`}
      style={active ? { borderColor: accent } : undefined}
    >
      <span
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
        style={{ backgroundColor: `${accent}1a`, color: accent }}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-800 dark:text-white">{title}</span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">{desc}</span>
      </span>
    </button>
  );
}
